'use client'

import { Link as I18nLink } from '@/i18n/navigation'
import NextLink from 'next/link'
import { useHeaderState } from './header/useHeaderState'
import { HeaderLogo } from './header/HeaderLogo'
import { HeaderNav } from './header/HeaderNav'
import { HeaderActions } from './header/HeaderActions'
import { HeaderSidebars } from './header/HeaderSidebars'
import { HeaderMobile } from './header/HeaderMobile'
import { HeaderMobileSubmenus } from './header/HeaderMobileSubmenus'
import './Header.css'

/**
 * Main Header Component
 *
 * Refactored from monolithic 936-line file to modular architecture.
 * Now orchestrates 11 sub-components for better maintainability.
 *
 * Architecture:
 * - useHeaderState: Custom hook with all state and event logic
 * - HeaderLogo: Logo display
 * - HeaderNav: Desktop navigation with dropdown triggers
 * - HeaderActions: Language switcher and CTA buttons
 * - HeaderSidebars: Three desktop sidebars (Lodges, Adventures, Menu)
 * - HeaderMobile: Mobile menu overlay
 * - HeaderMobileSubmenus: Mobile submenus for Lodges and Adventures
 *
 * @see components/header/ for implementation details
 */
export default function Header() {
  // Extract all state and logic from custom hook
  const {
    headerLoaded,
    navbarVisible,
    navbarDark,
    mobileMenuOpen,
    mobileSubmenuOpen,
    setMobileSubmenuOpen,
    headerRef,
    isI18n,
    isHomepage,
    t,
    tHeader,
    tFooter,
    tLodgesRegions,
    handleMobileMenuLinkClick,
  } = useHeaderState()

  // Use appropriate Link component based on context
  const Link = isI18n ? I18nLink : NextLink

  // Build header classes dynamically
  const headerClasses = [
    'header',
    headerLoaded && 'loaded',
    !isHomepage && 'instant-visible',
    (!isHomepage || headerLoaded) && (navbarVisible ? 'navbar-showing' : 'navbar-hiding'),
    navbarDark && 'navbar-dark',
    mobileMenuOpen && 'mobile-menu-open',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {/* Main Header */}
      <header ref={headerRef} className={headerClasses}>
        <nav className="nav-container">
          <HeaderLogo Link={Link} />
          <HeaderNav t={t} tHeader={tHeader} />
          <HeaderActions isI18n={isI18n} t={t} Link={Link} />
        </nav>
      </header>

      {/* Desktop Sidebars */}
      <HeaderSidebars
        t={t}
        tHeader={tHeader}
        tFooter={tFooter}
        tLodgesRegions={tLodgesRegions}
        Link={Link}
      />

      {/* Mobile Menu */}
      <HeaderMobile
        mobileMenuOpen={mobileMenuOpen}
        setMobileSubmenuOpen={setMobileSubmenuOpen}
        handleMobileMenuLinkClick={handleMobileMenuLinkClick}
        isI18n={isI18n}
        t={t}
      />

      {/* Mobile Submenus */}
      <HeaderMobileSubmenus
        mobileSubmenuOpen={mobileSubmenuOpen}
        setMobileSubmenuOpen={setMobileSubmenuOpen}
        handleMobileMenuLinkClick={handleMobileMenuLinkClick}
        t={t}
        tHeader={tHeader}
        tFooter={tFooter}
      />
    </>
  )
}
